// Per-device feature flag overrides. Layers on top of shop.feature_flags so
// a single browser can hide/show features without changing the shop-wide
// setting that affects every other device.
//
// Storage is scoped by shop_id — switching shops on one machine doesn't drag
// the previous shop's overrides into the next one.

import { useEffect, useState } from 'react';

export type FlagKey = 'quote' | 'sms' | 'inspect' | 'repair' | 'rec';
export type PlatformKey = 'web' | 'extension';
export type Tri = 'on' | 'off';
export type ShopFlags = Partial<Record<FlagKey, boolean>>;
export type DeviceOverrides = Partial<Record<PlatformKey, Partial<Record<FlagKey, Tri>>>>;

export const FLAG_KEYS: FlagKey[] = ['inspect', 'repair', 'quote', 'sms', 'rec'];

// Opt-in flags default OFF (unlike the default-on convention below) — they only
// turn on with an explicit per-device 'on' override. `rec` (on-creation RO job
// recommendations in chat) is dev/opt-in only, matching the extension's gate.
export const OPT_IN_FLAG_KEYS = new Set<FlagKey>(['rec']);

const STORAGE_KEY = (shopId: number | null | undefined) =>
  `haist:ff-overrides:${shopId ?? 'anon'}`;

export function getDeviceOverrides(shopId: number | null | undefined): DeviceOverrides {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY(shopId));
    return raw ? (JSON.parse(raw) as DeviceOverrides) : {};
  } catch {
    return {};
  }
}

export function setDeviceOverride(
  shopId: number | null | undefined,
  platform: PlatformKey,
  flag: FlagKey,
  value: Tri | null,
): DeviceOverrides {
  const next = { ...getDeviceOverrides(shopId) };
  const pmap = { ...(next[platform] ?? {}) };
  if (value === null) delete pmap[flag];
  else pmap[flag] = value;
  if (Object.keys(pmap).length === 0) delete next[platform];
  else next[platform] = pmap;
  saveOverrides(shopId, next);
  return next;
}

export function clearDeviceOverrides(shopId: number | null | undefined): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY(shopId));
  // localStorage events don't fire in the tab that made the change — emit our own
  window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY(shopId), newValue: null }));
}

function saveOverrides(shopId: number | null | undefined, overrides: DeviceOverrides): void {
  if (typeof localStorage === 'undefined') return;
  const key = STORAGE_KEY(shopId);
  if (Object.keys(overrides).length === 0) localStorage.removeItem(key);
  else localStorage.setItem(key, JSON.stringify(overrides));
  window.dispatchEvent(new StorageEvent('storage', { key, newValue: localStorage.getItem(key) }));
}

export function hasAnyOverride(overrides: DeviceOverrides | undefined): boolean {
  if (!overrides) return false;
  return Object.values(overrides).some((m) => m && Object.keys(m).length > 0);
}

// Merge shop-level flags with device overrides. Same shape consumers already use.
export function effectiveFlags(
  platform: PlatformKey,
  shopFlags: ShopFlags | undefined,
  overrides: DeviceOverrides | undefined,
): Record<FlagKey, boolean> {
  const pOverrides = overrides?.[platform] ?? {};
  const out: Record<FlagKey, boolean> = {} as Record<FlagKey, boolean>;
  for (const flag of FLAG_KEYS) {
    const o = pOverrides[flag];
    if (o === 'on') out[flag] = true;
    else if (o === 'off') out[flag] = false;
    else if (OPT_IN_FLAG_KEYS.has(flag)) out[flag] = false; // opt-in: default OFF
    else out[flag] = shopFlags?.[flag] !== false; // default-on convention
  }
  return out;
}

// React hook — re-renders when localStorage for this shop changes (other tabs + same tab via dispatched event).
export function useDeviceOverrides(shopId: number | null | undefined): DeviceOverrides {
  const [overrides, setOverrides] = useState<DeviceOverrides>(() => getDeviceOverrides(shopId));

  useEffect(() => {
    setOverrides(getDeviceOverrides(shopId));
    const key = STORAGE_KEY(shopId);
    const handler = (e: StorageEvent) => {
      if (e.key && e.key !== key) return;
      setOverrides(getDeviceOverrides(shopId));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [shopId]);

  return overrides;
}
